import React, { useState, useEffect, useCallback } from 'react';
import '../styles/reports.css';
import '../styles/users.css';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface AuditLog {
  id: number;
  user_id: number;
  user_email: string;
  action: string;
  resource_type: string;
  resource_id: string;
  details: Record<string, any> | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

const RESOURCE_TYPES = [
  'user', 'client', 'campaign', 'group', 'sub_group', 'agency', 'office',
  'invitation', 'discount_tier', 'settings',
];

const ACTION_TYPES = [
  'create_user', 'update_user', 'delete_user', 'block_user', 'unblock_user',
  'create_client', 'update_client', 'delete_client',
  'create_campaign', 'update_campaign', 'pause_campaign', 'activate_campaign',
  'create_group', 'update_group', 'delete_group',
  'create_sub_group', 'update_sub_group', 'delete_sub_group',
  'create_agency', 'update_agency', 'delete_agency',
  'create_office', 'update_office', 'delete_office',
  'create_invitation', 'cancel_invitation',
  'update_discount_tier',
  'login', 'login_failed', 'login_blocked', 'logout', 'update_settings',
];

const PAGE_SIZE = 50;

// Actions and resource types are stored values. Look the name up by value and fall
// back to the old Title Case of the raw value for anything we have no key for.
const titleCase = (s: string): string => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
function formatAction(action: string): string {
  return i18n.t(`audit.action_${action}`, { defaultValue: titleCase(action) });
}
function formatResource(resource: string): string {
  return i18n.t(`audit.resource_${resource}`, { defaultValue: titleCase(resource) });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseUserAgent(ua: string): string {
  // Browser
  let browser = '';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else browser = i18n.t('audit.browserGeneric');   // unrecognised UA — the rest are product names

  // OS
  let os = '';
  if (ua.includes('Mac OS X')) os = 'macOS';
  else if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  return os ? `${browser} / ${os}` : browser;
}

function getDetailsSummary(log: AuditLog): string | null {
  const parts: string[] = [];

  // User agent info for login actions
  if (log.user_agent) {
    parts.push(parseUserAgent(log.user_agent));
  }

  // Details from JSON
  if (log.details && Object.keys(log.details).length > 0) {
    for (const [k, v] of Object.entries(log.details)) {
      const val = typeof v === 'object' ? JSON.stringify(v) : String(v);
      parts.push(`${k.replace(/_/g, ' ')}: ${val}`);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

function getActionColor(action: string): string {
  if (action.startsWith('create') || action.startsWith('approve')) return '#27ae60';
  if (action.startsWith('update')) return '#2980b9';
  if (action.startsWith('delete') || action.startsWith('block') || action === 'login_blocked') return '#c0392b';
  if (action.startsWith('pause') || action.startsWith('archive')) return '#e67e22';
  if (action.startsWith('activate') || action.startsWith('unblock')) return '#16a085';
  if (action === 'login') return '#8e44ad';
  if (action === 'login_failed') return '#c0392b';
  if (action === 'logout') return '#7f8c8d';
  if (action.startsWith('cancel')) return '#d35400';
  return '#546e7a';
}

const AuditLogPage: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [emailSearch, setEmailSearch] = useState('');
  const [debouncedEmail, setDebouncedEmail] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');

  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [geoCache, setGeoCache] = useState<Record<string, string>>({});
  const [geoLoading, setGeoLoading] = useState<Record<string, boolean>>({});

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  const lookupGeo = useCallback(async (ip: string) => {
    if (geoCache[ip] || geoLoading[ip]) return;
    if (ip.startsWith('172.') || ip.startsWith('10.') || ip.startsWith('192.168.') || ip === '127.0.0.1') {
      setGeoCache(prev => ({ ...prev, [ip]: i18n.t('audit.privateNetwork') }));
      return;
    }
    setGeoLoading(prev => ({ ...prev, [ip]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/geo-lookup/${ip}`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      const geo = data.location || i18n.t('audit.unknownLocation');
      setGeoCache(prev => ({ ...prev, [ip]: geo }));
    } catch {
      setGeoCache(prev => ({ ...prev, [ip]: i18n.t('audit.lookupError') }));
    } finally {
      setGeoLoading(prev => ({ ...prev, [ip]: false }));
    }
  }, [geoCache, geoLoading]);

  // Debounce email search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedEmail(emailSearch), 400);
    return () => clearTimeout(timer);
  }, [emailSearch]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String((page - 1) * PAGE_SIZE));
      if (debouncedEmail.trim()) params.set('user_email', debouncedEmail.trim());
      if (actionFilter) params.set('action', actionFilter);
      if (resourceFilter) params.set('resource_type', resourceFilter);

      const response = await fetch(`${API_BASE}/api/admin/users/audit-logs?${params}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      console.error('Failed to load audit logs', err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedEmail, actionFilter, resourceFilter, getAuthHeaders]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedEmail, actionFilter, resourceFilter]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const clearFilters = () => {
    setEmailSearch('');
    setActionFilter('');
    setResourceFilter('');
  };

  const hasFilters = emailSearch || actionFilter || resourceFilter;

  return (
    <>
      {/* Header */}
      <div className="header">
        <div className="header-title">
          <h1>{t('audit.title')}</h1>
          <p className="header-subtitle">{t('audit.subtitle')}</p>
        </div>
      </div>

      <div className="reports-layout">
        {/* Filters Panel */}
        <aside className="filters-panel">
          <div className="filter-section">
            <h3 className="section-heading">{t('common.search')}</h3>
            <div className="campaign-search-input-wrapper">
              <svg className="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="campaign-search-input"
                placeholder={t('audit.searchEmail')}
                value={emailSearch}
                onChange={e => setEmailSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('audit.action')}</h3>
            <select
              className="form-select"
              value={actionFilter}
              onChange={e => setActionFilter(e.target.value)}
            >
              <option value="">{t('audit.allActions')}</option>
              {ACTION_TYPES.map(a => (
                <option key={a} value={a}>{formatAction(a)}</option>
              ))}
            </select>
          </div>

          <div className="filter-section">
            <h3 className="section-heading">{t('audit.resource')}</h3>
            <select
              className="form-select"
              value={resourceFilter}
              onChange={e => setResourceFilter(e.target.value)}
            >
              <option value="">{t('audit.allResources')}</option>
              {RESOURCE_TYPES.map(r => (
                <option key={r} value={r}>{formatResource(r)}</option>
              ))}
            </select>
          </div>

          {hasFilters && (
            <div className="filter-section" style={{ marginTop: '8px' }}>
              <button className="btn-secondary" style={{ width: '100%' }} onClick={clearFilters}>
                {t('billing.clearFilters')}
              </button>
            </div>
          )}
        </aside>

        {/* Results Area */}
        <div className="results-area visible">
          {loading ? (
            <div className="users-loading">
              <div className="users-spinner" />
            </div>
          ) : logs.length === 0 ? (
            <div className="editor-empty">
              <div className="editor-empty-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                  <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2v6h6" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" strokeLinecap="round"/>
                  <path d="M16 17H8" strokeLinecap="round"/>
                  <path d="M10 9H8" strokeLinecap="round"/>
                </svg>
              </div>
              <h3>{t('audit.noneFound')}</h3>
              <p>{t(hasFilters ? 'billing.tryAdjusting' : 'audit.willAppear')}</p>
            </div>
          ) : (
            <>
              <div className="users-table-container">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th style={{ width: '140px' }}>{t('audit.colDateTime')}</th>
                      <th style={{ width: '180px' }}>{t('audit.colUser')}</th>
                      <th style={{ width: '130px' }}>{t('audit.action')}</th>
                      <th style={{ width: '100px' }}>{t('audit.resource')}</th>
                      <th style={{ width: '100px' }}>IP</th>
                      <th>{t('audit.colDetails')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(log => {
                      const summary = getDetailsSummary(log);
                      return (
                        <tr key={log.id}>
                          <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>{formatDate(log.created_at)}</td>
                          <td>
                            <span style={{ fontSize: '13px' }}>{log.user_email || '—'}</span>
                          </td>
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 500,
                              background: getActionColor(log.action),
                              color: '#fff',
                            }}>
                              {formatAction(log.action)}
                            </span>
                          </td>
                          <td style={{ fontSize: '13px' }}>
                            {log.resource_type ? formatResource(log.resource_type) : '—'}
                            {log.resource_id && (
                              <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                                #{log.resource_id}
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                            {log.ip_address ? (
                              <div>
                                <span
                                  style={{ fontFamily: 'monospace', cursor: 'pointer' }}
                                  onClick={() => lookupGeo(log.ip_address!)}
                                  title={t('audit.lookupLocation')}
                                >
                                  {log.ip_address}
                                </span>
                                {geoLoading[log.ip_address] && (
                                  <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>...</div>
                                )}
                                {geoCache[log.ip_address] && (
                                  <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: '2px' }}>
                                    {geoCache[log.ip_address]}
                                  </div>
                                )}
                              </div>
                            ) : '—'}
                          </td>
                          <td style={{ fontSize: '13px' }}>
                            {summary ? (
                              expandedId === log.id ? (
                                <div>
                                  {log.user_agent && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                      {parseUserAgent(log.user_agent)}
                                    </div>
                                  )}
                                  {log.details && Object.keys(log.details).length > 0 && (
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px', background: '#f5f5f5', padding: '8px', borderRadius: '4px', maxWidth: '400px' }}>
                                      {JSON.stringify(log.details, null, 2)}
                                    </pre>
                                  )}
                                  <button
                                    onClick={() => setExpandedId(null)}
                                    style={{ border: 'none', background: 'none', color: '#999', cursor: 'pointer', fontSize: '11px', padding: '2px 0', marginTop: '4px' }}
                                  >
                                    {t('audit.collapse')}
                                  </button>
                                </div>
                              ) : (
                                <span
                                  onClick={() => setExpandedId(log.id)}
                                  style={{ cursor: 'pointer', color: 'var(--text-secondary)', maxWidth: '300px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                  title={t('audit.clickToExpand')}
                                >
                                  {summary}
                                </span>
                              )
                            ) : (
                              <span style={{ color: '#ccc' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', padding: '20px 0' }}>
                  <button
                    className="btn-secondary"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    {t('audit.prevPage')}
                  </button>
                  <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {t('audit.pageOf', { page, total: totalPages })}
                  </span>
                  <button
                    className="btn-secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => p + 1)}
                    style={{ padding: '8px 16px', fontSize: '13px' }}
                  >
                    {t('audit.nextPage')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AuditLogPage;
